import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  PhoneCall,
  Mail,
  HelpCircle,
  Send,
  CheckCircle2,
  Clock,
  Sparkles,
  MessageSquare,
} from "lucide-react";
import { apiPublic } from "@/lib/api";
import { VerifiedMobileInput } from "@/components/common/VerifiedInput";

export default function SupportPage() {
  const [settings, setSettings] = useState({
    support_phone: "+919876543210",
    support_email: "support@tablezy.com",
  });

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    cafe_name: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    apiPublic.get("/public/settings")
      .then(({ data }) => {
        if (data) setSettings((prev) => ({ ...prev, ...data }));
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanMob = form.mobile.replace(/\D/g, "");
    if (!form.name.trim() || cleanMob.length !== 10 || !form.message.trim()) {
      return toast.error("Please fill in your name, valid 10-digit mobile number, and support message.");
    }

    setSubmitting(true);
    try {
      await apiPublic.post("/public/support", form);
      setSubmitted(true);
      toast.success("Support message sent successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit support request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Help & Technical Assistance</span>
        <h1 className="font-heading text-4xl sm:text-6xl font-bold text-slate-900">Need Help?</h1>
        <p className="text-slate-600 text-base sm:text-lg">
          Our team is here to help you with your Tablezy QR menu. Reach out directly or drop us a message below.
        </p>
      </div>

      {/* Prominent Call Support Banner */}
      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white rounded-3xl p-8 sm:p-10 shadow-xl border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-5 text-center md:text-left">
          <div className="w-16 h-16 rounded-2xl bg-primary text-white grid place-items-center shrink-0 mx-auto md:mx-0 shadow-lg shadow-primary/30">
            <PhoneCall className="w-8 h-8 stroke-[2]" />
          </div>
          <div className="space-y-1">
            <div className="text-xs font-mono uppercase tracking-wider text-primary font-semibold">Direct Helpline</div>
            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white">{settings.support_phone}</h3>
            <p className="text-slate-400 text-xs">Available Mon–Sat: 9:00 AM – 9:00 PM IST</p>
          </div>
        </div>

        <a
          href={`tel:${settings.support_phone}`}
          className="h-14 px-8 rounded-full bg-primary text-primary-foreground text-base font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 hover:scale-105 active:scale-95 transition-all shadow-xl shadow-primary/25 shrink-0"
        >
          <PhoneCall className="w-5 h-5" />
          <span>Call Support Now ({settings.support_phone})</span>
        </a>
      </div>

      {/* Grid: Support Form & FAQ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Support Request Form */}
        <div className="lg:col-span-7 bg-card border border-border rounded-3xl p-8 shadow-sm">
          {submitted ? (
            <div className="py-12 text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-500 grid place-items-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="space-y-2">
                <h3 className="font-heading text-2xl font-bold text-slate-900">Message Received!</h3>
                <p className="text-slate-600 text-sm max-w-sm mx-auto">
                  Our technical support desk has received your ticket and will contact you via mobile shortly.
                </p>
              </div>
              <button
                onClick={() => setSubmitted(false)}
                className="h-11 px-6 rounded-full border border-border text-sm font-semibold hover:bg-slate-100 transition-colors"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="font-heading text-2xl font-bold text-slate-900">Support Message Bar</h3>
                <p className="text-slate-500 text-xs mt-1">Submit your query directly to our support team.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 font-semibold mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Vikramaditya"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-border bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium transition-all"
                  />
                </div>

                <VerifiedMobileInput
                  label="Mobile Number * (10-Digit Verification)"
                  value={form.mobile}
                  onChange={(val) => setForm({ ...form, mobile: val })}
                />
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 font-semibold mb-2">
                  Cafe / Restaurant Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Carolina Lounge"
                  value={form.cafe_name}
                  onChange={(e) => setForm({ ...form, cafe_name: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-border bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 font-semibold mb-2">
                  How Can We Help You? *
                </label>
                <textarea
                  rows={4}
                  required
                  placeholder="Describe your question, menu update request, or technical issue..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full p-4 rounded-xl border border-border bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full h-14 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-all disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                <span>{submitting ? "Sending Message..." : "Send Message"}</span>
              </button>
            </form>
          )}
        </div>

        {/* Support FAQ */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-slate-900 text-white rounded-3xl p-6 space-y-4">
            <h3 className="font-heading text-xl font-bold flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" /> Frequently Asked Questions
            </h3>
            <div className="space-y-4 text-xs sm:text-sm text-slate-300">
              <div className="border-b border-slate-800 pb-3 space-y-1">
                <div className="font-semibold text-white">How fast can I update my menu prices?</div>
                <p className="text-slate-400">Updates saved in the system reflect on all table QR scans instantly within seconds.</p>
              </div>
              <div className="border-b border-slate-800 pb-3 space-y-1">
                <div className="font-semibold text-white">Do customers need to install an app?</div>
                <p className="text-slate-400">No. Diners simply point their smartphone camera at the table QR code and the menu opens in their web browser.</p>
              </div>
              <div className="space-y-1">
                <div className="font-semibold text-white">Can I get printed physical QR cards for tables?</div>
                <p className="text-slate-400">Yes! We provide print-ready, table-numbered QR artwork for your acrylic stands or table stickers.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
